// src/pages/orgvisits/AddOrgVisit.js
import React, { useEffect, useState } from 'react';
import { Card, Container, Row, Col, Form, Button } from 'react-bootstrap';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { TextField } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdRefresh } from 'react-icons/md';
import './orgvisits.css';

dayjs.extend(customParseFormat);

const AddOrgVisit = () => {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  
  const [editId, setEditId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const userStr = sessionStorage.getItem("user");
  const userObj = userStr ? JSON.parse(userStr) : {};
  const school_id = userObj.school_id || null;
  const username = userObj.user_name || userObj.username || 'admin';

  // Initial Form State
  const [form, setForm] = useState({
    org_name: '',
    visitor_name: '',
    contact_info: '',
    visit_date: dayjs().format('YYYY-MM-DD'),
    arrival_time: null,
    departure_time: null,
    purpose: '',
    details: '',
    followup_required: false,
    followup_action: '',
    followup_target_date: '',
    status: 'Pending'
  });

  // FORCE SCROLL FIX: Ensure parent .pageBody allows vertical scrolling
  useEffect(() => {
    const pageBody = document.querySelector('.pageBody');
    if (pageBody) {
      pageBody.style.overflowY = 'auto';
      pageBody.style.maxHeight = 'calc(100vh - 90px)';
    }
    return () => {
      if (pageBody) {
        pageBody.style.overflowY = '';
        pageBody.style.maxHeight = '';
      }
    };
  }, []);

  // Parse time safely from string or dayjs object
  const parseVisitTime = (v) => {
    if (!v) return null;
    if (dayjs.isDayjs(v)) return v;
    const parsed = dayjs(v, ['h:mm A', 'hh:mm A', 'H:mm', 'HH:mm', 'HH:mm:ss'], true);
    return parsed.isValid() ? parsed : null;
  };

  // Check if editing a record
  useEffect(() => {
    const vd = routeLocation.state?.visitData;
    if (!vd) return;
    
    setEditId(vd.visit_id ?? null);
    setForm({
      org_name: vd.org_name || '',
      visitor_name: vd.visitor_name || '',
      contact_info: vd.contact_info || '',
      visit_date: vd.visit_date || dayjs().format('YYYY-MM-DD'),
      arrival_time: parseVisitTime(vd.arrival_time),
      departure_time: parseVisitTime(vd.departure_time),
      purpose: vd.purpose || '',
      details: vd.details || '',
      followup_required: vd.followup_required ?? false,
      followup_action: vd.followup_action || '',
      followup_target_date: vd.followup_target_date || '',
      status: vd.status || 'Pending'
    });
    
    // Clear route state so refresh doesn't hold edit data
    navigate('.', { replace: true, state: {} });
  }, [routeLocation, navigate]);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { id, checked } = e.target;
    setForm((prev) => ({ ...prev, [id]: checked }));
  };

  // Convert dayjs time to minutes for time bounds verification
  const toMinutes = (t) => {
    if (dayjs.isDayjs(t) && t.isValid()) {
      return t.hour() * 60 + t.minute();
    }
    return null;
  };

  const handleReset = () => {
    setForm({
      org_name: '',
      visitor_name: '',
      contact_info: '',
      visit_date: dayjs().format('YYYY-MM-DD'),
      arrival_time: null,
      departure_time: null,
      purpose: '',
      details: '',
      followup_required: false,
      followup_action: '',
      followup_target_date: '',
      status: 'Pending'
    });
    toast.info('Form Reset Successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validation Guards
    const org = (form.org_name || '').trim();
    const visitor = (form.visitor_name || '').trim();

    if (!org) {
      toast.error('Organization Name is required.');
      return;
    }
    if (!visitor) {
      toast.error('Representative / Visitor Name is required.');
      return;
    }
    if (!form.purpose) {
      toast.error('Please select the Purpose of Visit.');
      return;
    }

    const arrTimeMin = toMinutes(form.arrival_time);
    const depTimeMin = toMinutes(form.departure_time);

    if (arrTimeMin === null || depTimeMin === null) {
      toast.error('Arrival Time and Departure Time are required.');
      return;
    }
    if (arrTimeMin >= depTimeMin) {
      toast.error('Arrival time must be earlier than departure time.');
      return;
    }

    if (form.followup_required) {
      if (!(form.followup_action || '').trim()) {
        toast.error('Please describe the follow-up action required.');
        return;
      }
      if (!form.followup_target_date) {
        toast.error('Please set a target date for the follow-up.');
        return;
      }
      if (form.followup_target_date && form.visit_date) {
        const visitDateObj = dayjs(form.visit_date);
        const targetDateObj = dayjs(form.followup_target_date);
        if (targetDateObj.isBefore(visitDateObj, 'day')) {
          toast.error('Follow-up target date cannot be earlier than the visit date.');
          return;
        }
      }
    }

    setIsLoading(true);

    // Formatted payload
    const payload = {
      org_name: org,
      visitor_name: visitor,
      contact_info: (form.contact_info || '').trim(),
      visit_date: form.visit_date,
      arrival_time: dayjs.isDayjs(form.arrival_time) ? form.arrival_time.format('hh:mm A') : '',
      departure_time: dayjs.isDayjs(form.departure_time) ? form.departure_time.format('hh:mm A') : '',
      purpose: form.purpose,
      details: (form.details || '').trim(),
      followup_required: form.followup_required,
      followup_action: form.followup_required ? (form.followup_action || '').trim() : '',
      followup_target_date: form.followup_required ? form.followup_target_date : '',
      status: form.status,
      school_id: school_id
    };

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      
      if (editId !== null) {
        // UPDATE ACTION
        await axios.post(`${baseUrl}/orgvisits/`, {
          action: 'UPDATE',
          visit_id: editId,
          lastmodifiedby: username,
          ...payload
        });
        toast.success('Organization Visit Log Updated Successfully!');
      } else {
        // CREATE ACTION
        await axios.post(`${baseUrl}/orgvisits/`, {
          action: 'CREATE',
          createdby: username,
          ...payload
        });
        toast.success('Organization Visit Logged Successfully!');
      }

      setTimeout(() => {
        setIsLoading(false);
        navigate('/orgvisits');
      }, 800); // Clean experience transition
    } catch (error) {
      console.error("Error saving organization visit log:", error);
      const errMsg = error.response?.data?.error || "Failed to save organization visit log.";
      toast.error(errMsg);
      setIsLoading(false);
    }
  };

  return (
    <div className="pageBody" style={{ overflowY: 'auto', height: 'calc(100vh - 100px)', paddingBottom: '40px' }}>
      <ToastContainer position="top-right" autoClose={3000} newestOnTop />
      
      <Container fluid>
        {/* Dynamic header title based on Add or Edit mode */}
        <div className="d-flex align-items-center mb-4 gap-3">
          <Button 
            className="secondaryBtn px-3 py-2 d-flex align-items-center gap-2" 
            onClick={() => navigate('/orgvisits')}
            style={{ border: 'none', background: 'transparent' }}
          >
            <MdArrowBack style={{ fontSize: '20px' }} /> Back to Logs
          </Button>
          <h4 style={{ margin: 0 }}>
            {editId !== null ? 'Modify Organization Visit Log' : 'Log New Organization Visit'}
          </h4>
        </div>

        <Card className="form-card">
          <Card.Body className="form-body">
            <Form onSubmit={handleSubmit}>
              <Row>
                {/* Left Form Segment - Visitor Details */}
                <Col xs={12} lg={6} style={{ paddingRight: '24px', borderRight: '1px solid #edf2f9' }}>
                  <h6 className="commonSectionTitle">
                    Visitor & Organization Details
                  </h6>
                  
                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Organization Name <span className="requiredStar">*</span></Form.Label>
                      <Form.Control
                        required
                        type="text"
                        id="org_name"
                        value={form.org_name}
                        placeholder="e.g. DU Inspection Board, Microsoft, Otis Elevators"
                        maxLength={150}
                        onChange={handleInputChange}
                        onKeyUp={(e) => {
                          const v = e.currentTarget.value.replace(/\s{2,}/g, ' ');
                          if (v !== form.org_name) setForm((p) => ({ ...p, org_name: v }));
                        }}
                      />
                    </Form.Group>
                  </div>

                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Representative Name <span className="requiredStar">*</span></Form.Label>
                      <Form.Control
                        required
                        type="text"
                        id="visitor_name"
                        value={form.visitor_name}
                        placeholder="e.g. Dr. Ramesh Sharma, Sarah Jenkins"
                        maxLength={150}
                        onChange={handleInputChange}
                        onKeyUp={(e) => {
                          const v = e.currentTarget.value.replace(/\s{2,}/g, ' ');
                          if (v !== form.visitor_name) setForm((p) => ({ ...p, visitor_name: v }));
                        }}
                      />
                    </Form.Group>
                  </div>

                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Contact Information</Form.Label>
                      <Form.Control
                        type="text"
                        id="contact_info"
                        value={form.contact_info}
                        placeholder="Email and/or Phone Number"
                        maxLength={255}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>

                  <Row>
                    <Col xs={12} sm={6}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Date of Visit <span className="requiredStar">*</span></Form.Label>
                          <Form.Control
                            required
                            type="date"
                            id="visit_date"
                            value={form.visit_date}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                    
                    <Col xs={12} sm={6}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Purpose of Visit <span className="requiredStar">*</span></Form.Label>
                          <Form.Select
                            required
                            id="purpose"
                            value={form.purpose}
                            onChange={handleInputChange}
                          >
                            <option value="">Select Purpose</option>
                            <option value="Audit/Inspection">Audit/Inspection</option>
                            <option value="Partnership">Partnership</option>
                            <option value="General Enquiry">General Enquiry</option>
                            <option value="Service/Repair">Service/Repair</option>
                            <option value="Guest Lecture">Guest Lecture</option>
                          </Form.Select>
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12} sm={6}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Arrival Time <span className="requiredStar">*</span></Form.Label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              ampm
                              minutesStep={1}
                              value={dayjs.isDayjs(form.arrival_time) ? form.arrival_time : null}
                              onChange={(val) => setForm((p) => ({ ...p, arrival_time: val }))}
                              renderInput={(params) => <TextField {...params} required fullWidth size="small" />}
                            />
                          </LocalizationProvider>
                        </Form.Group>
                      </div>
                    </Col>
                    
                    <Col xs={12} sm={6}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Departure Time <span className="requiredStar">*</span></Form.Label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <TimePicker
                              ampm
                              minutesStep={1}
                              value={dayjs.isDayjs(form.departure_time) ? form.departure_time : null}
                              onChange={(val) => setForm((p) => ({ ...p, departure_time: val }))}
                              renderInput={(params) => <TextField {...params} required fullWidth size="small" />}
                            />
                          </LocalizationProvider>
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>

                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Discussions & General Details</Form.Label>
                      <Form.Control
                        as="textarea"
                        id="details"
                        value={form.details}
                        placeholder="Log discussions, notes, requirements or feedback here..."
                        maxLength={600}
                        rows="4"
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </div>
                </Col>

                {/* Right Form Segment - Follow-ups Details */}
                <Col xs={12} lg={6} style={{ paddingLeft: '24px' }}>
                  <h6 className="commonSectionTitle">
                    Follow-up Actions & Status
                  </h6>

                  <div className="commonInput d-flex align-items-center mb-4">
                    <Form.Group className="d-flex align-items-center gap-3">
                      <Form.Label className="mb-0" style={{ cursor: 'pointer', fontWeight: 600, color: '#303133' }} htmlFor="followup_required">
                        Does this visit require a follow-up action?
                      </Form.Label>
                      <Form.Check 
                        type="switch"
                        id="followup_required"
                        className="custom-switch"
                        label=""
                        checked={form.followup_required}
                        onChange={handleCheckboxChange}
                      />
                    </Form.Group>
                  </div>

                  {/* Conditionally expanded follow-up section details */}
                  {form.followup_required && (
                    <div className="followup-section mb-4" style={{ display: 'block' }}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Follow-up Action Description <span className="requiredStar">*</span></Form.Label>
                          <Form.Control
                            as="textarea"
                            required={form.followup_required}
                            id="followup_action"
                            value={form.followup_action}
                            placeholder="Describe what our organization needs to execute next..."
                            maxLength={400}
                            rows="4"
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>

                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>Target Follow-up Execution Date <span className="requiredStar">*</span></Form.Label>
                          <Form.Control
                            required={form.followup_required}
                            type="date"
                            id="followup_target_date"
                            min={form.visit_date || dayjs().format('YYYY-MM-DD')} // Can only select dates from the visit date onwards
                            value={form.followup_target_date}
                            onChange={handleInputChange}
                          />
                        </Form.Group>
                      </div>
                    </div>
                  )}

                  <div className="commonInput">
                    <Form.Group>
                      <Form.Label>Status <span className="requiredStar">*</span></Form.Label>
                      <Form.Select
                        required
                        id="status"
                        value={form.status}
                        onChange={handleInputChange}
                      >
                        <option value="Pending">Pending</option>
                        <option value="In Progress">In Progress</option>
                        <option value="Completed">Completed</option>
                      </Form.Select>
                    </Form.Group>
                  </div>
                </Col>
              </Row>

              {/* Action Buttons Footer */}
              <div className="d-flex justify-content-between mt-4 pt-3" style={{ borderTop: '1px solid #edf2f9' }}>
                <div>
                  <Button
                    type="button"
                    variant="light"
                    className="secondaryBtn d-flex align-items-center gap-2"
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    <MdRefresh /> Reset
                  </Button>
                </div>
                <div className="d-flex gap-2">
                  <Button
                    type="button"
                    variant="danger"
                    className="secondaryBtn"
                    onClick={() => navigate('/orgvisits')}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="primaryBtn d-flex align-items-center gap-2"
                    disabled={isLoading}
                  >
                    <MdSave /> {isLoading ? 'Saving...' : editId !== null ? 'Update Log' : 'Save Log'}
                  </Button>
                </div>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AddOrgVisit;
